import React, { useCallback, useEffect, useRef, useState } from 'react';
import eventBus from '../EventBus';
import gsap from 'gsap';

type LoadingProps = {};

// Minimum time (ms) the preloader stays visible
const MIN_DISPLAY_TIME = 3500;

const LoadingScreen: React.FC<LoadingProps> = () => {
    const [progress, setProgress] = useState(0);
    const [webGLError, setWebGLError] = useState(false);
    const [webGLErrorOpacity, setWebGLErrorOpacity] = useState(0);

    const hasStartedExitRef = useRef(false);
    const mountTimeRef = useRef(Date.now());
    const resourcesDoneRef = useRef(false);
    const introCompleteRef = useRef(false);

    const displayCounterRef = useRef({ value: 0 });
    const counterTextRef = useRef<HTMLSpanElement>(null);
    const progressFillRef = useRef<HTMLDivElement>(null);
    const fakeProgressRef = useRef({ value: 0 });

    const overlayRef = useRef<HTMLDivElement>(null);
    const contentRef = useRef<HTMLDivElement>(null);
    const letterHRef = useRef<HTMLSpanElement>(null);
    const letterKRef = useRef<HTMLSpanElement>(null);
    const progressBarRef = useRef<HTMLDivElement>(null);
    const labelRef = useRef<HTMLDivElement>(null);
    const dotRef = useRef<HTMLSpanElement>(null);

    const tryExit = useCallback(() => {
        if (hasStartedExitRef.current) return;
        if (!resourcesDoneRef.current) return;
        if (!introCompleteRef.current) return;

        const elapsed = Date.now() - mountTimeRef.current;
        const remaining = Math.max(0, MIN_DISPLAY_TIME - elapsed);

        setTimeout(() => {
            if (hasStartedExitRef.current) return;
            hasStartedExitRef.current = true;

            // Snap counter to 100% before exit
            gsap.to(displayCounterRef.current, {
                value: 100,
                duration: 0.5,
                ease: 'power2.out',
                onUpdate: () => {
                    if (counterTextRef.current) {
                        counterTextRef.current.textContent =
                            Math.round(displayCounterRef.current.value) + '%';
                    }
                },
            });
            gsap.to(progressFillRef.current, {
                scaleX: 1,
                duration: 0.5,
                ease: 'power2.out',
                onComplete: () => {
                    // Small pause at 100% then exit
                    setTimeout(() => exitAnimation(), 300);
                },
            });
        }, remaining);
    }, []);

    // Intro animation
    useEffect(() => {
        const urlParams = new URLSearchParams(window.location.search);
        if (urlParams.has('debug')) {
            hasStartedExitRef.current = true;
            exitAnimation();
            return;
        }

        if (!detectWebGLContext()) {
            setWebGLError(true);
            return;
        }

        // Set initial hidden states
        gsap.set(letterHRef.current, { y: 120, opacity: 0 });
        gsap.set(letterKRef.current, { y: 120, opacity: 0 });
        gsap.set(dotRef.current, { scale: 0, opacity: 0 });
        gsap.set(labelRef.current, { opacity: 0, y: 20 });
        gsap.set(progressBarRef.current, { opacity: 0 });

        const tl = gsap.timeline({
            delay: 0.5,
            onComplete: () => {
                introCompleteRef.current = true;
                tryExit();
            },
        });

        // H rises up
        tl.to(letterHRef.current, {
            y: 0,
            opacity: 1,
            duration: 1.4,
            ease: 'power4.out',
        });

        // K rises up with stagger
        tl.to(
            letterKRef.current,
            {
                y: 0,
                opacity: 1,
                duration: 1.4,
                ease: 'power4.out',
            },
            '-=0.9'
        );

        // Dot bounces in
        tl.to(
            dotRef.current,
            {
                scale: 1,
                opacity: 1,
                duration: 0.6,
                ease: 'back.out(2)',
            },
            '-=0.6'
        );

        // Label fades in
        tl.to(
            labelRef.current,
            {
                opacity: 1,
                y: 0,
                duration: 0.8,
                ease: 'power3.out',
            },
            '-=0.4'
        );

        // Progress bar fades in
        tl.to(
            progressBarRef.current,
            {
                opacity: 1,
                duration: 0.6,
                ease: 'power3.out',
            },
            '-=0.5'
        );

        // Start smooth fake progress bar fill (over MIN_DISPLAY_TIME)
        // This gives a smooth 0→90% fill regardless of actual load speed
        gsap.to(fakeProgressRef.current, {
            value: 90,
            duration: MIN_DISPLAY_TIME / 1000,
            ease: 'power1.inOut',
            delay: 1.5, // start after letters appear
            onUpdate: () => {
                const fakeVal = fakeProgressRef.current.value;
                const realVal = Math.round(progress * 100);
                const displayVal = Math.max(fakeVal, realVal);

                if (counterTextRef.current) {
                    counterTextRef.current.textContent =
                        Math.round(displayVal) + '%';
                }
                if (progressFillRef.current) {
                    gsap.set(progressFillRef.current, {
                        scaleX: displayVal / 100,
                    });
                }
            },
        });
    }, []);

    // Listen for resource loading events
    useEffect(() => {
        eventBus.on('loadedSource', (data: any) => {
            setProgress(data.progress);
        });
    }, []);

    // When progress hits 100%
    useEffect(() => {
        if (progress >= 1 && !webGLError) {
            resourcesDoneRef.current = true;
            tryExit();
        }
    }, [progress, webGLError]);

    useEffect(() => {
        if (webGLError) {
            setTimeout(() => setWebGLErrorOpacity(1), 500);
        }
    }, [webGLError]);

    const exitAnimation = useCallback(() => {
        const overlay = overlayRef.current;
        const content = contentRef.current;
        if (!overlay || !content) {
            eventBus.dispatch('loadingScreenDone', {});
            const ui = document.getElementById('ui');
            if (ui) ui.style.pointerEvents = 'none';
            return;
        }

        const tl = gsap.timeline({
            onComplete: () => {
                eventBus.dispatch('loadingScreenDone', {});
                const ui = document.getElementById('ui');
                if (ui) ui.style.pointerEvents = 'none';
                overlay.style.display = 'none';
            },
        });

        // Step 1: Fade out label + progress bar (slide down)
        tl.to([labelRef.current, progressBarRef.current], {
            opacity: 0,
            y: 30,
            duration: 0.5,
            ease: 'power3.in',
        });

        // Step 2: Dot shrinks away
        tl.to(
            dotRef.current,
            {
                scale: 0,
                opacity: 0,
                duration: 0.3,
                ease: 'power2.in',
            },
            '-=0.2'
        );

        // Step 3: ENTIRE content div zooms in to fill and beyond the screen
        // This scales everything together from center — fills the whole viewport
        tl.to(
            content,
            {
                scale: 20,
                opacity: 0,
                duration: 1.8,
                ease: 'power3.inOut',
            },
            '-=0.1'
        );

        // Step 4: Overlay background fades with blur
        tl.to(
            overlay,
            {
                opacity: 0,
                filter: 'blur(10px)',
                duration: 0.6,
                ease: 'power2.inOut',
            },
            '-=1.0'
        );
    }, []);

    const detectWebGLContext = () => {
        var canvas = document.createElement('canvas');
        var gl =
            canvas.getContext('webgl') ||
            canvas.getContext('experimental-webgl');
        if (gl && gl instanceof WebGLRenderingContext) return true;
        return false;
    };

    return (
        <div ref={overlayRef} style={styles.overlay}>
            {/* WebGL Error */}
            {webGLError && (
                <div
                    style={Object.assign({}, styles.errorContainer, {
                        opacity: webGLErrorOpacity,
                    })}
                >
                    <div style={styles.errorPopup}>
                        <p
                            style={{
                                color: 'red',
                                fontWeight: 'bold',
                                fontFamily: 'monospace',
                            }}
                        >
                            CRITICAL ERROR: No WebGL Detected
                        </p>
                        <div style={{ height: 16 }} />
                        <p style={{ fontFamily: 'monospace', color: '#fff' }}>
                            WebGL is required to run this site. Please enable it
                            or switch to a supported browser.
                        </p>
                    </div>
                </div>
            )}

            {/* Preloader */}
            {!webGLError && (
                <div ref={contentRef} style={styles.content}>
                    {/* HK Monogram */}
                    <div style={styles.monogram}>
                        <span ref={letterHRef} style={styles.letterH}>
                            H
                        </span>
                        <span ref={letterKRef} style={styles.letterK}>
                            K
                        </span>
                        <span ref={dotRef} style={styles.dot} />
                    </div>

                    {/* Label row */}
                    <div ref={labelRef} style={styles.label}>
                        <span style={styles.labelText}>LOADING</span>
                        <span
                            ref={counterTextRef}
                            style={styles.labelCounter}
                        >
                            0%
                        </span>
                    </div>

                    {/* Progress bar */}
                    <div ref={progressBarRef} style={styles.progressBar}>
                        <div
                            ref={progressFillRef}
                            style={styles.progressFill}
                        />
                    </div>
                </div>
            )}
        </div>
    );
};

const styles: StyleSheetCSS = {
    overlay: {
        backgroundColor: '#0a0a0a',
        width: '100%',
        height: '100%',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        boxSizing: 'border-box',
        transformOrigin: 'center center',
        willChange: 'transform, opacity, filter',
        position: 'relative',
        overflow: 'hidden',
    },
    content: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        position: 'relative',
        zIndex: 10,
        willChange: 'transform, opacity',
        transformOrigin: 'center center',
    },
    monogram: {
        display: 'flex',
        alignItems: 'baseline',
        position: 'relative',
        marginBottom: '56px',
    },
    letterH: {
        fontFamily: "'Inter', 'Helvetica Neue', Arial, sans-serif",
        fontSize: 'clamp(100px, 20vw, 220px)',
        fontWeight: 900,
        color: '#ffffff',
        letterSpacing: '0.02em',
        lineHeight: 1,
        display: 'inline-block',
        willChange: 'transform, opacity',
        transformOrigin: 'center center',
    },
    letterK: {
        fontFamily: "'Inter', 'Helvetica Neue', Arial, sans-serif",
        fontSize: 'clamp(100px, 20vw, 220px)',
        fontWeight: 900,
        color: '#888888',
        letterSpacing: '0.02em',
        lineHeight: 1,
        display: 'inline-block',
        willChange: 'transform, opacity',
        transformOrigin: 'center center',
    },
    dot: {
        position: 'absolute',
        bottom: '18px',
        right: '-24px',
        width: '14px',
        height: '14px',
        borderRadius: '50%',
        backgroundColor: '#c9a84c',
        display: 'inline-block',
        willChange: 'transform, opacity',
        transformOrigin: 'center center',
    },
    label: {
        display: 'flex',
        justifyContent: 'space-between',
        width: '280px',
        marginBottom: '10px',
        willChange: 'transform, opacity',
    },
    labelText: {
        fontFamily: "'Inter', 'Helvetica Neue', Arial, sans-serif",
        fontSize: '11px',
        fontWeight: 400,
        color: 'rgba(255, 255, 255, 0.35)',
        letterSpacing: '0.35em',
        textTransform: 'uppercase',
    },
    labelCounter: {
        fontFamily: "'Inter', 'Helvetica Neue', Arial, sans-serif",
        fontSize: '11px',
        fontWeight: 500,
        color: 'rgba(255, 255, 255, 0.6)',
        letterSpacing: '0.1em',
        fontVariantNumeric: 'tabular-nums',
    },
    progressBar: {
        width: '280px',
        height: '2px',
        backgroundColor: 'rgba(255, 255, 255, 0.08)',
        borderRadius: '2px',
        overflow: 'hidden',
        willChange: 'opacity',
    },
    progressFill: {
        width: '100%',
        height: '100%',
        backgroundColor: '#c9a84c',
        borderRadius: '2px',
        transformOrigin: 'left center',
        transform: 'scaleX(0)',
        willChange: 'transform',
    },
    errorContainer: {
        position: 'absolute',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        transition: 'opacity 0.5s ease',
    },
    errorPopup: {
        backgroundColor: '#1a1a2e',
        padding: 24,
        border: '3px solid red',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        maxWidth: 500,
    },
};

export default LoadingScreen;
